import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import {
  Calendar,
  ChartPie,
  ClipboardList,
  GanttChartSquare,
  KanbanSquare,
  LayoutList,
  Search,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { debounce } from "@/lib/utils";

import { WorkspaceToggle } from "@/components/workspace-toggle";

import {
  FilterButton,
  SortButton,
  AttributeDataType,
  serializeFiltersToQuery,
  serializeSortsToQuery,
  parseFiltersFromQuery,
  parseSortsFromQuery,
  type FilterableAttribute,
  type SortableAttribute,
  type DynamicFilter,
  type DynamicSort,
} from "@/components/search-filter-bar";

import TabsComponent from "@/components/tabs/tabs-component";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { GetColumnInfo } from "@/hooks/services/pps/handlers/dynamic-table-api";
import { useColumnInfoStore } from "@/stores/column-info";
import { usePermissions } from "@/hooks/use-permissions";
import { useUserProfileStore } from "@/stores/user-profile-store";
import { Card, CardContent } from "@/components/ui/card";
import AiButtonDrawer from "./components/ai-button-drawer/ai-button-drawer-component";
import { PLANNER_PERMISSIONS } from "@/constants/permission";

// Helper function to map column types to AttributeDataType
function mapColumnTypeToDataType(columnType: string): AttributeDataType {
  switch (columnType?.toLowerCase()) {
    case "text":
    case "string":
      return AttributeDataType.TEXT;
    case "int":
    case "integer":
    case "number":
      return AttributeDataType.INT;
    case "decimal":
    case "float":
    case "fraction":
      return AttributeDataType.FRACTION;
    case "date":
    case "range":
    case "datetime":
    case "timestamp":
      return AttributeDataType.DATETIME;
    case "boolean":
    case "bool":
      return AttributeDataType.BOOLEAN;
    case "foreignkey":
    case "fk":
    case "select":
    case "multiselect":
      return AttributeDataType.FOREIGN_KEY;
    default:
      return AttributeDataType.TEXT;
  }
}

export const Route = createFileRoute("/(planner-screens)/planner")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      filters: (search.filters as string) || undefined,
      sorts: (search.sorts as string) || undefined,
      search: (search.search as string) || undefined,
    };
  },
  // WOR-1793 Req #2: Redirect to plant-3/list if no Plant1_ViewJob permission
  beforeLoad: () => {
    const { userProfile } = useUserProfileStore.getState();
    if (userProfile?.rolePermissions) {
      const hasPermission = userProfile.rolePermissions.includes(
        PLANNER_PERMISSIONS.PLANT1_VIEW_JOB,
      );
      if (!hasPermission) {
        throw redirect({ to: "/plant-3/list" });
      }
    }
  },
});

function RouteComponent() {
  const { pathname } = useLocation();
  const { setColumnSchema } = useColumnInfoStore();
  const router = useRouter();
  const permissions = usePermissions(); // WOR-1793: Add permission hook

  const { data } = useGetData(
    GetColumnInfo({
      tableName: "Project Tasks",
      workspaceId: 1,
      page: 0,
      pageSize: 0,
    }),
  );

  // Read current search params
  const currentSearch = router.state.location.search;

  // Read current filters and sorts from URL
  const parsedFilters = currentSearch.filters
    ? parseFiltersFromQuery(currentSearch.filters as string)
    : [];

  const dynamicFilters: DynamicFilter[] = useMemo(() => {
    const reverseMappedFilters = parsedFilters.map((f: any) => {
      if (f.filterBy && data?.data?.columnsSchema) {
        const column = data.data.columnsSchema.find(
          (col: any) => col.filter?.by === f.filterBy && col.filter?.active,
        );
        if (column) {
          return {
            ...f,
            filterBy: column.name, // Use UI field name
          };
        }
      }
      return f;
    });

    // Validate and filter out invalid filter objects (must have filterBy, type)
    return reverseMappedFilters.filter((f: any) => {
      const isValid =
        f && typeof f.filterBy === "string" && typeof f.type === "string";
      if (!isValid) {
        console.warn("Invalid filter object found and removed:", f);
      }
      return isValid;
    });
  }, [parsedFilters, data?.data?.columnsSchema]);

  const parsedSorts = currentSearch.sorts
    ? parseSortsFromQuery(currentSearch.sorts as string)
    : [];

  // Reverse map backend field names to UI field names for sorts
  const dynamicSorts: DynamicSort[] = useMemo(() => {
    return parsedSorts.map((s: any) => {
      if (s.field && data?.data?.columnsSchema) {
        const column = data.data.columnsSchema.find(
          (col: any) => col.sort?.by === s.field && col.sort?.active,
        );
        if (column) {
          return {
            ...s,
            field: column.name,
          };
        }
      }
      return s;
    });
  }, [parsedSorts, data?.data?.columnsSchema]);

  // Handler functions to update filters/sorts in URL
  const updateURL = useCallback(
    (updates: { filters?: DynamicFilter[]; sorts?: DynamicSort[] }) => {
      const currentSearchParams = router.state.location.search;
      const newSearch: any = { ...currentSearchParams };

      if (updates.filters !== undefined) {
        // Map UI field names to backend field names before serialization
        const mappedFilters = updates.filters.map((filter) => {
          if (filter.filterBy && data?.data?.columnsSchema) {
            const column = data.data.columnsSchema.find(
              (col: any) => col.name === filter.filterBy,
            );
            if (column?.filter?.by && column.filter.active) {
              return {
                ...filter,
                filterBy: column.filter.by,
              };
            }
          }
          return filter;
        });

        const serialized = serializeFiltersToQuery(mappedFilters);

        if (serialized) {
          newSearch.filters = serialized;
        } else {
          delete newSearch.filters;
        }
      }

      if (updates.sorts !== undefined) {
        // Map UI field names to backend field names for sorts before serialization
        const mappedSorts = updates.sorts.map((sort) => {
          if (sort.field && data?.data?.columnsSchema) {
            const column = data.data.columnsSchema.find(
              (col: any) => col.name === sort.field,
            );
            if (column?.sort?.by && column.sort.active) {
              return {
                ...sort,
                field: column.sort.by,
              };
            }
          }
          return sort;
        });

        const serialized = serializeSortsToQuery(mappedSorts);
        if (serialized) {
          newSearch.sorts = serialized;
        } else {
          delete newSearch.sorts;
        }
      }

      router.navigate({
        to: pathname,
        search: newSearch,
        replace: true,
      });
    },
    [pathname, router, data?.data?.columnsSchema],
  );

  const addFilter = useCallback(() => {
    const newFilter: DynamicFilter = {
      filterBy: "",
      type: "contains",
      value: "",
    };

    updateURL({ filters: [...dynamicFilters, newFilter] });
  }, [dynamicFilters, updateURL]);

  const updateFilter = useCallback(
    (index: number, updates: Partial<DynamicFilter>) => {
      const updated = dynamicFilters.map((f, i) => {
        if (i === index) {
          return { ...f, ...updates };
        }
        return f;
      });
      updateURL({ filters: updated });
    },
    [dynamicFilters, updateURL],
  );

  const removeFilter = useCallback(
    (index: number) => {
      const filtered = dynamicFilters.filter((_, i) => i !== index);
      updateURL({ filters: filtered });
    },
    [dynamicFilters, updateURL],
  );

  const clearFilters = useCallback(() => {
    updateURL({ filters: [] });
  }, [updateURL]);

  const addSort = useCallback(
    (attribute: SortableAttribute) => {
      const newSort: DynamicSort = {
        field: attribute.field,
        direction: 0, // Default to ASC
        nestedField: attribute.nestedField,
      };
      updateURL({ sorts: [...dynamicSorts, newSort] });
    },
    [dynamicSorts, updateURL],
  );

  const updateSort = useCallback(
    (index: number, updates: Partial<DynamicSort>) => {
      const updated = dynamicSorts.map((s, i) =>
        i === index ? { ...s, ...updates } : s,
      );
      updateURL({ sorts: updated });
    },
    [dynamicSorts, updateURL],
  );

  const removeSort = useCallback(
    (index: number) => {
      const filtered = dynamicSorts.filter((_, i) => i !== index);
      updateURL({ sorts: filtered });
    },
    [dynamicSorts, updateURL],
  );

  const clearSorts = useCallback(() => {
    updateURL({ sorts: [] });
  }, [updateURL]);

  useEffect(() => {
    if (data?.data) {
      setColumnSchema(
        data.data.columnsSchema.map((column: any) => {
          if (
            column.type === "multiselect" &&
            column?.options &&
            column?.optionKey
          ) {
            column.options = column?.options?.map((option: any) => ({
              value: option[column?.optionKey?.value]?.toString(),
              label: option[column?.optionKey?.label],
            }));
          }

          return column;
        }),
      );
    }
  }, [data?.data, setColumnSchema]);

  // Convert columnsSchema to filterable/sortable attributes
  const filterableAttributes: FilterableAttribute[] =
    data?.data?.columnsSchema
      ?.filter((col: any) => col.filter?.active)
      ?.map((col: any) => ({
        id: col.name,
        field: col.name,
        label: col.label,
        dataType: mapColumnTypeToDataType(col.type),
        nestedField: col.filter?.nestedField,
        options: col.options || undefined,
      })) || [];

  const sortableAttributes: SortableAttribute[] =
    data?.data?.columnsSchema
      ?.filter((col: any) => col.sort?.active)
      ?.map((col: any) => ({
        id: col.name,
        field: col.name,
        label: col.label,
        nestedField: col.sort?.nestedField,
      })) || [];

  const prefixPathname = pathname.split("/").slice(1, 2).join("/");

  const handleChangeValue = useCallback(
    (value: string) => {
      const currentSearchParams = router.state.location.search;
      const newSearch: any = { ...currentSearchParams };

      if (value) {
        newSearch.search = value;
      } else {
        delete newSearch.search;
      }

      router.navigate({
        to: pathname,
        search: newSearch,
        replace: true,
      });
    },
    [pathname, router],
  );

  // Memoize debounced handler to prevent creating new function on each render
  const debouncedHandleChangeValue = useMemo(
    () => debounce(handleChangeValue, 500),
    [handleChangeValue],
  );

  const tabs = [
    {
      icon: <ChartPie className="aero:h-4 aero:w-4" />,
      label: "Status Overview",
      direction: `/${prefixPathname}/status-overview`,
    },
    {
      icon: <LayoutList className="aero:h-4 aero:w-4" />,
      label: "List",
      direction: `/${prefixPathname}/list`,
    },
    {
      icon: <Calendar className="aero:h-4 aero:w-4" />,
      label: "Calendar",
      direction: `/${prefixPathname}/calendar`,
    },
    {
      icon: <GanttChartSquare className="aero:h-4 aero:w-4" />,
      label: "Gantt",
      direction: `/${prefixPathname}/gantt`,
    },
    {
      icon: <KanbanSquare className="aero:h-4 aero:w-4" />,
      label: "Kanban",
      direction: `/${prefixPathname}/kanban`,
    },
    {
      icon: <Users className="aero:h-4 aero:w-4" />,
      label: "Planner - Man Power",
      direction: `/${prefixPathname}/man-power`,
    },
  ];

  const addOnsButtonComponent = [
    {
      component: <AiButtonDrawer />,
    },
    {
      component: (
        <FilterButton
          filterableAttributes={filterableAttributes}
          dynamicFilters={dynamicFilters}
          onAddFilter={addFilter}
          onUpdateFilter={updateFilter}
          onRemoveFilter={removeFilter}
          onClearFilters={clearFilters}
          className="aero:mt-0"
        />
      ),
    },
    {
      component: (
        <SortButton
          sortableAttributes={sortableAttributes}
          dynamicSorts={dynamicSorts}
          onAddSort={addSort}
          onUpdateSort={updateSort}
          onRemoveSort={removeSort}
          onClearSorts={clearSorts}
          className="aero:mt-0"
        />
      ),
    },
    {
      component: (
        <AnimatedSearch
          onChange={debouncedHandleChangeValue}
          value={(currentSearch?.search as string) || ""}
        />
      ),
    },
  ];

  return (
    <div className="aero:flex aero:flex-col aero:gap-4 aero:w-full aero:max-w-full aero:px-4 aero:overflow-hidden">
      {/* Page Title */}
      <div className="aero:flex aero:items-center aero:justify-between">
        <div className="aero:flex aero:items-center aero:gap-3">
          <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
            <ClipboardList className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
          </div>
          <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
            Production Planning
          </h1>
        </div>
        <WorkspaceToggle />
      </div>

      {/* Main Card */}
      <Card className="aero:!rounded-lg aero:!border-neutral-300 aero:!p-4 aero:!gap-2 aero:!shadow-none">
        {/* Tabs Header */}
        <div className="aero:pb-4 aero:border-b aero:border-neutral-400">
          <TabsComponent
            tabs={tabs}
            activeTab={pathname.slice(1)}
            addOnsButtons={addOnsButtonComponent}
            withAiButton={permissions.canManagePlant1()}
            tabListClassName={"aero:min-w-0 aero:overflow-x-auto"}
          />
        </div>

        {/* Content */}
        <CardContent className="aero:!p-0 aero:!pt-4 aero:min-w-0 aero:overflow-x-auto">
          <Outlet />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Create Simple animated search component
 * @param param0
 * @returns
 */

function AnimatedSearch({
  onChange,
  value,
}: {
  onChange: (value: string) => void;
  value: string;
}) {
  const [isOpen, setIsOpen] = useState(!!value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange(value);
  };
  return (
    <div
      className={`aero:flex aero:items-center aero:transition-all aero:duration-800 aero:ease-in-out ${isOpen ? "aero:w-[240px]" : "aero:w-10 "}`}
    >
      <div
        className={`aero:mr-2 aero:transition-all aero:duration-800 aero:ease-in-out ${
          isOpen ? "aero:w-[200px] aero:opacity-100" : "aero:w-0 aero:opacity-0"
        }`}
      >
        <Input
          ref={inputRef}
          type="search"
          placeholder="Search ..."
          className="aero:w-full"
          defaultValue={value}
          onChange={handleChange}
          style={{ padding: isOpen ? "" : "0" }}
        />
      </div>
      <Button
        intent="custom"
        design="text"
        size="iconMd"
        onClick={() => setIsOpen(!isOpen)}
        className="aero:h-8 aero:w-8 aero:shrink-0"
      >
        <Search className="aero:h-4 aero:w-4" />
        <span className="aero:sr-only">Toggle search</span>
      </Button>
    </div>
  );
}
